import { useForm } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toastFlash } from '@/lib/flash-toast';

export function CreateSponsorDialog({ eventSlug }: { eventSlug: string }) {
  const [open, setOpen] = useState(false);
  const form = useForm({
    slug: '',
    name: '',
    url: '',
    logoUrl: '',
  });
  const { data, setData, processing, reset } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.post(`/admin/${eventSlug}/sponsors`, {
      preserveScroll: true,
      onSuccess: (page) => {
        toastFlash(page, 'Sponsor created successfully!');
        reset();
        setOpen(false);
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="mr-2 h-4 w-4" />
        Add Sponsor
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Sponsor</DialogTitle>
            <DialogDescription>
              Add a new sponsor for this event.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="create-sponsor-slug">Slug</Label>
              <Input
                id="create-sponsor-slug"
                placeholder="openai"
                disabled={processing}
                value={data.slug}
                onChange={(e) => setData('slug', e.target.value)}
              />
              {errors.slug && (
                <p className="text-destructive text-sm">{errors.slug}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-sponsor-name">Name</Label>
              <Input
                id="create-sponsor-name"
                placeholder="OpenAI"
                disabled={processing}
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
              />
              {errors.name && (
                <p className="text-destructive text-sm">{errors.name}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-sponsor-url">URL (Optional)</Label>
              <Input
                id="create-sponsor-url"
                placeholder="https://openai.com"
                disabled={processing}
                value={data.url}
                onChange={(e) => setData('url', e.target.value)}
              />
              {errors.url && (
                <p className="text-destructive text-sm">{errors.url}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="create-sponsor-logo-url">
                Logo URL (Optional)
              </Label>
              <Input
                id="create-sponsor-logo-url"
                placeholder="https://..."
                disabled={processing}
                value={data.logoUrl}
                onChange={(e) => setData('logoUrl', e.target.value)}
              />
              {errors.logoUrl && (
                <p className="text-destructive text-sm">{errors.logoUrl}</p>
              )}
            </div>

            {errors.base && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4">
                <p className="text-red-600 text-sm">{errors.base}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
